// WAAP per-domain config resources — generic GET/PUT over /<area>.
//
// Mirrors the manticore `useCdnConfig` / `useUpdateCdnConfig` factory style, but
// for the WAAP surface: responses are bare (no `{ data }` envelope), an
// un-configured/unknown resource surfaces as a 400/404 → `null`, and writes
// invalidate the matching aspect key. Feature tabs call these with the concrete
// DTO type, e.g. `useWaapAspect<WaapMode>(domainId, WAAP_AREAS.mode)`.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, type WaapArea } from './_shared';

/** Read a WAAP config aspect. Returns `null` when the vendor has no config yet
 *  (400/404) so callers can fall back to their defaults. */
export function useWaapAspect<T>(domainId: string | undefined, area: WaapArea) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', area),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<T | null> => {
      try {
        const res = await serviceApiV3.get<T>(waapPath(domainId as string, area));
        return res.data ?? null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

/** Upsert a WAAP config aspect (PUT). Invalidates the aspect on success. */
export function useUpdateWaapAspect<T>(
  domainId: string,
  area: WaapArea,
  options?: UseMutationOptions<unknown, unknown, T>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: T) => {
      const res = await serviceApiV3.put(waapPath(domainId, area), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, area) });
      options?.onSuccess?.(...args);
    },
  });
}
